import { cn } from 'cn';
import { type PropsWithChildren, type ReactElement } from 'react';

import { KeyInformation, type KeyInformationItem } from './KeyInformation';

export type KeyInformationGroupItem = {
  readonly children: KeyInformationItem[];
  readonly groupName: string;
} & ComponentStyling;

export type KeyInformationRowItem =
  | KeyInformationItem
  | KeyInformationGroupItem;

type CommonProps = PropsWithChildren<{
  readonly hasDividers?: boolean;
  readonly hasHighContrast?: boolean;
  readonly valueSize?: 'default' | 'large';
}> &
  ComponentStyling;

type FlatKeyInformationRowProps = CommonProps & {
  readonly items: Array<KeyInformationItem | undefined>;
};

type P = CommonProps & {
  readonly items: Array<KeyInformationRowItem | undefined>;
};

function isKeyInformationGroup(
  item: KeyInformationRowItem,
): item is KeyInformationGroupItem {
  return 'groupName' in item;
}

function getRenderedItemCount(item: KeyInformationRowItem) {
  return isKeyInformationGroup(item) ? item.children.length : 1;
}

function getGroupFlex(itemCount: number) {
  const gapBasis = Math.max(0, itemCount - 1) * 2;

  return `${itemCount} 1 ${gapBasis}rem`;
}

export function KeyInformationRow(
  props: FlatKeyInformationRowProps,
): ReactElement;
export function KeyInformationRow(props: P): ReactElement;
export function KeyInformationRow({
  children,
  className,
  hasDividers = false,
  hasHighContrast = false,
  items,
  style,
  valueSize,
}: P) {
  const definedItems = items.filter(
    (item): item is KeyInformationRowItem => item !== undefined,
  );
  const renderedItemIndexes = definedItems.reduce<number[]>(
    (indexes, item) => {
      indexes.push((indexes.at(-1) ?? 0) + getRenderedItemCount(item));

      return indexes;
    },
    [0],
  );

  return (
    <div
      className={cn('flex items-start gap-x-8 overflow-x-auto', className)}
      style={style}
    >
      {definedItems.map((item, index) => {
        const renderedItemIndex = renderedItemIndexes[index] ?? 0;

        if (isKeyInformationGroup(item)) {
          const { children, className, groupName, style } = item;

          return (
            <div
              className={cn('flex min-w-0 flex-col', className)}
              data-key-information-group
              key={groupName}
              style={{ flex: getGroupFlex(children.length), ...style }}
            >
              <div className="flex min-w-0 gap-x-8">
                {children.map((child, childIndex) => (
                  <KeyInformation
                    key={child.label}
                    {...child}
                    className={cn('flex-auto', child.className)}
                    hasDivider={
                      hasDividers && (childIndex > 0 || renderedItemIndex > 0)
                    }
                    hasHighContrast={hasHighContrast}
                    valueSize={valueSize}
                  />
                ))}
              </div>
              <span
                className={cn(
                  '-mt-1.5 border-t-2 border-tiga-border-lighter pt-1 text-center text-xs',
                  hasHighContrast
                    ? 'text-tiga-text-default'
                    : 'text-tiga-text-lighter',
                )}
              >
                {groupName}
              </span>
            </div>
          );
        }

        return (
          <KeyInformation
            key={item.label}
            {...item}
            hasDivider={hasDividers && renderedItemIndex > 0}
            hasHighContrast={hasHighContrast}
            valueSize={valueSize}
          />
        );
      })}
      {children}
    </div>
  );
}
